import type {
  FieldMappingConfigResponse,
  UpdateFieldMappingConfigRequest,
} from "@/types/fieldMappingConfig";

const MOCK_FIELD_MAPPING_CONFIG: FieldMappingConfigResponse = {
  site_id: "Dd2w9Da",
  version: 0,
  fields: {
    "custom:product_bullets": {
      status: "ignored",
      reason:
        "Rich text HTML containing multiple, varied product features in an unordered list. Requires complex parsing to extract specific canonical fields.",
    },
    "custom_fields:child_category": {
      status: "unmappable",
      reason: "Category slug, no canonical field.",
    },
    "custom_fields:enable_pdp_marquee": {
      status: "unmappable",
      reason: "Boolean flag, no corresponding canonical field.",
    },
    "custom_fields:enable_product_features": {
      status: "unmappable",
      reason: "Boolean flag, no corresponding canonical field.",
    },
    "custom_fields:hide_reviews": {
      status: "unmappable",
      reason: "Boolean flag, no corresponding canonical field.",
    },
    "custom_fields:hide_size_guide": {
      status: "unmappable",
      reason: "Boolean flag, no corresponding canonical field.",
    },
    "custom_fields:parent_category": {
      status: "unmappable",
      reason: "Category slug, no canonical field.",
    },
    "custom_fields:pdp_-_shipping__returns": {
      status: "mapped",
      outputs: [
        {
          internal_field: "shipping_returns",
          json_path: "0.pdp_-_shipping__returns",
          cleanups: ["strip_html", "collapse_whitespace", "trim"],
          confidence: 0.9,
        },
      ],
    },
    "custom_fields:product_details_list": {
      status: "unmappable",
      reason:
        "Internal list of identifiers or indices, not product details themselves.",
    },
    "custom_fields:product_details_list_item": {
      status: "deferred",
      reason:
        "Contains multiple, varied product features (e.g., 'colder/longer', 'chills for X hours', 'leakproof lid') that require complex, per-item parsing or mapping to multiple canonical fields. Cannot be extracted with a single json_path and cleanup sequence.",
    },
    "custom_fields:shop_all_collections_featured": {
      status: "unmappable",
      reason: "Boolean flag, no corresponding canonical field.",
    },
    "custom_fields:shop_all_featured": {
      status: "unmappable",
      reason: "Boolean flag, no corresponding canonical field.",
    },
    "filter:collection": {
      status: "unmappable",
      reason: "List of filter values, not a canonical product attribute.",
    },
    "filter:color": {
      status: "unmappable",
      reason:
        "List of available colors for filtering; canonical 'color' refers to the primary product color.",
    },
    "filter:drinks": {
      status: "unmappable",
      reason: "List of filter values, not a canonical product attribute.",
    },
    "mc-facebook:condition": {
      status: "unmappable",
      reason: "Product condition (e.g., 'new'), no canonical field.",
    },
    "mc-facebook:google_product_category": {
      status: "unmappable",
      reason: "External system category ID, no canonical field.",
    },
    "msft_bingads:product_status": {
      status: "unmappable",
      reason: "External system-specific product status, no canonical field.",
    },
    "reviews:rating": {
      status: "unmappable",
      reason: "No canonical field for product rating score.",
    },
    "reviews:rating_count": {
      status: "unmappable",
      reason: "No canonical field for review count.",
    },
    "yotpo:bottomline": {
      status: "unmappable",
      reason: "HTML content related to review bottom line, no canonical field.",
    },
    "yotpo:bottomline_update_time": {
      status: "unmappable",
      reason: "Timestamp, no corresponding canonical field.",
    },
    "yotpo:catalog_bottomline": {
      status: "unmappable",
      reason: "HTML content related to review bottom line, no canonical field.",
    },
    "yotpo:catalog_bottomline_update_time": {
      status: "unmappable",
      reason: "Timestamp, no corresponding canonical field.",
    },
    "yotpo:main_widget_update_time": {
      status: "unmappable",
      reason: "Timestamp, no corresponding canonical field.",
    },
    "yotpo:qa_bottomline": {
      status: "unmappable",
      reason: "HTML content related to Q&A bottom line, no canonical field.",
    },
    "yotpo:qa_bottomline_update_time": {
      status: "unmappable",
      reason: "Timestamp, no corresponding canonical field.",
    },
    "yotpo:reviews_average": {
      status: "unmappable",
      reason: "No canonical field for average review rating.",
    },
    "yotpo:reviews_count": {
      status: "unmappable",
      reason: "No canonical field for review count.",
    },
    "yotpo:richsnippetshtml": {
      status: "unmappable",
      reason:
        "HTML content related to rich snippets for reviews, no canonical field.",
    },
    "additional_colors:products": {
      status: "unmappable",
      reason:
        "List of product references for additional colors, not a canonical product attribute.",
    },
    "c_f:product_column_1_image": {
      status: "unmappable",
      reason:
        "Image URL for UI presentation, no canonical field for this purpose.",
    },
    "c_f:product_column_1_title": {
      status: "unmappable",
      reason:
        "Product feature title for UI presentation, not a canonical product attribute.",
    },
    "c_f:product_column_2_image": {
      status: "unmappable",
      reason:
        "Image URL for UI presentation, no canonical field for this purpose.",
    },
    "c_f:product_column_2_title": {
      status: "unmappable",
      reason:
        "Product feature title for UI presentation, not a canonical product attribute.",
    },
    "c_f:product_column_3_image": {
      status: "unmappable",
      reason:
        "Image URL for UI presentation, no canonical field for this purpose.",
    },
    "c_f:product_column_3_title": {
      status: "unmappable",
      reason:
        "Product feature title for UI presentation, not a canonical product attribute.",
    },
    "c_f:product_layout_video": {
      status: "unmappable",
      reason: "Video URL, no canonical field for this purpose.",
    },
    "c_f:product_related_skus": {
      status: "unmappable",
      reason:
        "Refers to related product SKUs, not a canonical product attribute.",
    },
    "c_f:product_social_feed_slug": {
      status: "unmappable",
      reason:
        "Product social feed slug/identifier, not a canonical product attribute.",
    },
    "custom_fields:collage_gallery": {
      status: "unmappable",
      reason:
        "Gallery of image URLs for UI presentation, no canonical field for this purpose.",
    },
    "custom_fields:marquee_background_color": {
      status: "unmappable",
      reason: "UI background color, not a canonical product attribute.",
    },
    "custom_fields:marquee_text_color": {
      status: "unmappable",
      reason: "UI text color, not a canonical product attribute.",
    },
    "custom_fields:pdp_marquee_text": {
      status: "unmappable",
      reason:
        "Marketing/descriptive text for a UI element, not a canonical product attribute.",
    },
    "custom_fields:product_column_1_image": {
      status: "unmappable",
      reason:
        "Image URL for UI presentation, no canonical field for this purpose.",
    },
    "custom_fields:product_column_1_title": {
      status: "unmappable",
      reason:
        "Product feature title for UI presentation, not a canonical product attribute.",
    },
    "custom_fields:product_column_2_image": {
      status: "unmappable",
      reason:
        "Image URL for UI presentation, no canonical field for this purpose.",
    },
    "custom_fields:product_column_2_title": {
      status: "unmappable",
      reason:
        "Product feature title for UI presentation, not a canonical product attribute.",
    },
    "custom_fields:product_column_3_image": {
      status: "unmappable",
      reason:
        "Image URL for UI presentation, no canonical field for this purpose.",
    },
    "custom_fields:product_column_3_title": {
      status: "unmappable",
      reason:
        "Product feature title for UI presentation, not a canonical product attribute.",
    },
    "custom_fields:product_description": {
      status: "unmappable",
      reason:
        "General product description text; too broad and unstructured for a single canonical field.",
    },
    "custom_fields:product_lifestyle_image": {
      status: "unmappable",
      reason: "Image URL, no canonical field for this purpose.",
    },
    "custom_fields:product_related_skus": {
      status: "unmappable",
      reason:
        "Refers to related product SKUs, not a canonical product attribute.",
    },
    "custom_fields:product_social_feed_slug": {
      status: "unmappable",
      reason:
        "Product social feed slug/identifier, not a canonical product attribute.",
    },
    "custom_fields:recommended_products": {
      status: "unmappable",
      reason:
        "Refers to related product identifiers/SKUs, not a canonical product attribute.",
    },
    "filter:attributes": {
      status: "unmappable",
      reason:
        "Generic list of product attributes/features, no direct canonical field.",
    },
    "filter:size": {
      status: "mapped",
      outputs: [{ internal_field: "capacity", json_path: "0" }],
    },
    "global:description_tag": {
      status: "unmappable",
      reason: "SEO meta description tag, not a canonical product attribute.",
    },
    "global:title_tag": {
      status: "unmappable",
      reason: "SEO title tag, not a canonical product attribute.",
    },
    "yotpo_reviews:1001": {
      status: "unmappable",
      reason: "Yotpo reviews HTML, not a canonical product attribute.",
    },
    "additional_colors:title": {
      status: "unmappable",
      reason:
        "Seems like a product or variant title/description, no direct canonical field.",
    },
    "additional_colors:toggle": {
      status: "unmappable",
      reason: "Internal flag, not a customer-facing product attribute.",
    },
    "custom:color_group_title": {
      status: "unmappable",
      reason:
        "Describes a color group or finish title, not a specific color attribute.",
    },
    "custom_fields:faqs": {
      status: "unmappable",
      reason:
        "Structured FAQ content, no canonical field for this type of information.",
    },
    "custom_fields:pdp_-_product_dimensions": {
      status: "mapped",
      outputs: [
        {
          internal_field: "length",
          cleanups: [
            "regex_extract:Length:\\s*([\\d.]+)in",
            "to_number",
            "in_to_cm",
          ],
          unit_hint: "cm",
        },
        {
          internal_field: "width",
          cleanups: [
            "regex_extract:Width:\\s*([\\d.]+)in",
            "to_number",
            "in_to_cm",
          ],
          unit_hint: "cm",
        },
        {
          internal_field: "height",
          cleanups: [
            "regex_extract:Height:\\s*([\\d.]+)in",
            "to_number",
            "in_to_cm",
          ],
          unit_hint: "cm",
        },
        {
          internal_field: "weight",
          cleanups: [
            "regex_extract:Weight:\\s*([\\d.]+)lb",
            "to_number",
            "lb_to_kg",
          ],
          unit_hint: "kg",
        },
      ],
    },
  },
};

// In-memory overlay so mock updates persist across reads within a session.
const mockConfigStore = new Map<string, FieldMappingConfigResponse>();

/**
 * Clears the in-memory overlay. Tests that exercise the save/update flow should
 * call this in `beforeEach` so shared module state does not leak between cases
 * or across test files run in the same worker.
 */
export function resetMockConfigStore(): void {
  mockConfigStore.clear();
}

function cloneConfig(
  config: FieldMappingConfigResponse,
): FieldMappingConfigResponse {
  return JSON.parse(JSON.stringify(config)) as FieldMappingConfigResponse;
}

function baseMockConfig(
  siteId: string,
  vendorProductId: string,
): FieldMappingConfigResponse {
  if (siteId === "Dd2w9Da" && vendorProductId === "1234567890") {
    return cloneConfig(MOCK_FIELD_MAPPING_CONFIG);
  }
  return {
    site_id: siteId,
    version: 0,
    fields: {},
  };
}

export function resolveMockFieldMappingConfig(
  siteId: string,
  vendorProductId: string,
): FieldMappingConfigResponse {
  const stored = mockConfigStore.get(siteId);
  if (stored) return cloneConfig(stored);
  return baseMockConfig(siteId, vendorProductId);
}

/**
 * Applies the update payload onto a copy of the given config, returning the
 * merged result. Mappings not referenced in `updates` are preserved.
 *
 * Remove semantics:
 * - `remove: true` with `target_field` removes only that target output; sibling
 *   outputs on the source field are kept. If it was the last output, the source
 *   field entry is dropped.
 * - `remove: true` without `target_field` removes the whole source field entry.
 *
 * Add/update semantics:
 * - `target_field` (no `remove`) adds the target as an output, preserving any
 *   existing sibling outputs and replacing the matching output in place.
 *
 * Status semantics:
 * - `status` (e.g. `"ignored"`) sets the source field's status directly.
 *   Ignoring clears any outputs, since an ignored field maps to nothing.
 */
export function applyFieldMappingUpdates(
  config: FieldMappingConfigResponse,
  request: UpdateFieldMappingConfigRequest,
): FieldMappingConfigResponse {
  const next = cloneConfig(config);
  for (const update of request.updates) {
    const existing = next.fields[update.source_field];

    if (update.status) {
      next.fields[update.source_field] = {
        ...existing,
        status: update.status,
        ...(update.status === "ignored" ? { outputs: [] } : {}),
      };
      continue;
    }

    if (update.remove) {
      if (!update.target_field) {
        delete next.fields[update.source_field];
        continue;
      }
      if (!existing) continue;
      const remaining = (existing.outputs ?? []).filter(
        (o) => o.internal_field !== update.target_field,
      );
      if (remaining.length === 0) {
        delete next.fields[update.source_field];
      } else {
        next.fields[update.source_field] = { ...existing, outputs: remaining };
      }
      continue;
    }

    if (!update.target_field) continue;
    const output = {
      internal_field: update.target_field,
      ...(update.json_path ? { json_path: update.json_path } : {}),
      ...(update.cleanups ? { cleanups: update.cleanups } : {}),
      ...(update.unit_hint ? { unit_hint: update.unit_hint } : {}),
    };
    const siblings = (existing?.outputs ?? []).filter(
      (o) => o.internal_field !== update.target_field,
    );
    next.fields[update.source_field] = {
      status: "mapped",
      outputs: [...siblings, output],
    };
  }
  return next;
}

export function resolveMockUpdateFieldMappingConfig(
  siteId: string,
  request: UpdateFieldMappingConfigRequest,
): FieldMappingConfigResponse {
  // Mock reads are keyed by vendor product id; the update is site-level, so
  // seed the overlay from the default mock product config for this site.
  const current =
    mockConfigStore.get(siteId) ?? baseMockConfig(siteId, "1234567890");
  const updated = applyFieldMappingUpdates(current, request);
  mockConfigStore.set(siteId, updated);
  return cloneConfig(updated);
}
